import React, { useState, useMemo, useCallback } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { PieChartIcon, Xsl02Icon } from "@hugeicons/core-free-icons/index";
import { useTranslation } from "react-i18next";
import BtnAsLink from "@/Component/BtnAsLink";
import { TableC } from "@/CommonElements/TableC";
import { useBasicDataQuery } from "@/services/basicData/basicData";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { LabelValue } from "@/Component/Value";
import { useSystem } from "@/utils/hooks/useLocation";
import { useGetMakkahGroupsQuery } from "@/services/makkahGroups";
import { ImportDepartureGroupModal } from "./ImportDepartureGroupModal";
import { SYSTEMS } from "@/utils/permissions";

export const DepartureGroups = () => {
  const { t } = useTranslation();
  const { getLocalizedNumber, getLocalizedText } = useLocalizedText();
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { port, currentSystem } = useSystem();
  const [filters, setFilters] = useState({
    direction_id: currentSystem === SYSTEMS.MAKKAH.code ? 2 : 1,
  });
  const departureGroupBase = `/app/${currentSystem}/groups-departure`;
  const { data: basicDataRes } = useBasicDataQuery();
  const basicData = useMemo(() => basicDataRes?.data?.data || {}, [basicDataRes]);

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  // const {
  //   data: listRes,
  //   isLoading,
  // } = useGetMakkahDepartureGroupsQuery({
  //   search: debouncedValue,
  //   page: currentPage,
  //   per_page: 10,
  //   ...filters,
  // });

  const {
    data: listRes,
    isLoading,
    isFetching,
  } = useGetMakkahGroupsQuery({
    query: debouncedValue,
    page: currentPage,
    ...filters,
  });
  const rows = listRes?.data?.data || [];
  const meta = listRes?.data?.meta;
  const loading = isLoading || isFetching;

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const getServiceCenterName = useCallback(
    (code) => {
      if (!code) return "-";
      const serviceCenter = (basicData?.service_centers || []).find(
        (sc) => String(sc.code) === String(code)
      );
      return serviceCenter ? serviceCenter.name : code || "-";
    },
    [basicData]
  );

  const columns = useMemo(
    () => [
      {
        name: t("GROUP_NUMBER"),
        key: "group_number",
        render: (value, rowData) => (
          <Link
            className="fw-bold"
            to={`${departureGroupBase}/${rowData.id}/${port}/view`}
          >
            {getLocalizedNumber(value)}
          </Link>
        ),
      },
      {
        name: t("MAKKAH_GROUPS_COL_ORGANIZATION_COUNTRY"),
        key: "organizer_country",
        render: (value) => <SelectCountry.Display value={value} />,
      },
      {
        name: t("MAKKAH_GROUPS_COL_ORGANIZER_NAME"),
        key: "group_name",
        render: ($, rowData) =>
          [
            rowData?.delegation_detail?.id,
            getLocalizedText(
              rowData?.delegation_detail?.nameAr,
              rowData?.delegation_detail?.nameEn
            ),
          ].join(" - "),
      },
      {
        name: t("MAKKAH_GROUPS_COL_NATIONALITY"),
        key: "nationality",
        render: (value) => <SelectCountry.Display value={value} />,
      },
      {
        name: t("AFFILIATED_ORGANIZER"),
        key: "affiliated_organizer_detail",
        render: (value) =>
          getLocalizedText(value?.companyNameAr, value?.companyNameEn),
      },
      {
        name: t("ORGANIZER_NAME"),
        key: "supervisor_name",
        render: (value) => value || "-",
      },
      {
        name: t("MAPPED_GROUP_NAME"),
        key: "group_name_mapping_id",
        render: (value, rowData) => rowData?.pilgrim_group_name_mapping?.mapped_group_name || "-",
      },
      {
        name: t("MOBILE_NUMBER"),
        key: "supervisor_mobile_number",
        render: (value) => (
          <span dir="ltr">{getLocalizedNumber(value) || "-"}</span>
        ),
      },
      {
        name: t("MAKKAH_GROUPS_COL_SERVICE_CENTER"),
        key: "service_center_id",
        render: (value) => getServiceCenterName(value),
      },
      {
        name: t("MAKKAH_GROUPS_COL_TOTAL_PILGRIMS"),
        key: "total_pilgrims",
        filterable: false,
        render: ($, rowData) => {
          const totalPilgrims = (rowData?.details || []).reduce(
            (sum, d) => sum + (Number(d.capacity) || 0),
            0
          );
          return getLocalizedNumber(totalPilgrims) || "-";
        },
      },
      {
        name: t("UPCOMING_COL_ACTIONS"),
        key: "actions",
        filterable: false,
      },
    ],
    [t, getLocalizedText, getLocalizedNumber, departureGroupBase, port, getServiceCenterName]
  );

  const getActions = (rowData) => {
    const editUrl = `${departureGroupBase}/${rowData.id}/${port}/edit`;
    return [
      {
        label: (
          <Link to={editUrl} className="d-flex align-items-center gap-2 text-nowrap">
            <HugeiconsIcon size={18} icon={PieChartIcon} />
            <span>{t("MANAGE_SEGMENTS")}</span>
          </Link>
        ),
        key: "add-details",
      },
    ];
  };

  const addButton = (
    <BtnAsLink
      disabled={loading}
      onClick={() => setShowImportModal(true)}
      className="btn-primary"
      icon={<HugeiconsIcon icon={Xsl02Icon} />}
    >
      {t("IMPORT_DETAILS")}
    </BtnAsLink>
  );

  const filterFields = [
    {
      name: "date",
      label: t("FLIGHT_DATE"),
      type: "date",
    },
    {
      name: "group_number",
      label: t("GROUP_NUMBER"),
      type: "input",
    },
    {
      name: "nationality",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "organizer_country",
      label: t("ORGANIZER_COUNTRY"),
      type: "select",
      optionKey: "nationalities",
      options: (c) => ({
        value: c.id,
        label: <SelectCountry.Display value={c.id} />,
      }),
    },
    {
      name: "service_center_id",
      label: t("SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",
      options: (sc) => ({
        value: sc.id,
        label: sc.name,
      }),
    },
    {
      name: "makkah_contract_id",
      label: t("MAKKAH_CONTRACTS"),
      type: "select",
      optionKey: "contracts",
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "madinah_contract_id",
      label: t("MADINAH_CONTRACTS"),
      type: "select",
      optionKey: "contracts",
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "direction_id",
      label: t("DIRECTION"),
      type: "select",
      optionKey: "directions",
      options: (d) => ({
        value: d.id,
        label: d.name,
      }),
      filter: (d) => d.id === 1 || d.id === 2,
    },
  ];

  return (
    <Container fluid>
      <TableC
        data={rows}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("MAKKAH_GROUPS_SEARCH_PLACEHOLDER")}
        pagination={{
          current: currentPage || 1,
          total: meta?.total ?? 0,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
        }}
        actions={getActions}
        renderActions={addButton}
        filterFields={filterFields}
        filters={filters}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        emptyStateTitle={
          debouncedValue
            ? t("MAKKAH_GROUPS_NO_RESULTS")
            : t("MAKKAH_GROUPS_NO_GROUPS")
        }
        emptyStateDescription={
          debouncedValue
            ? t("MAKKAH_GROUPS_NO_RESULTS_DESCRIPTION")
            : t("MAKKAH_GROUPS_NO_GROUPS_DESCRIPTION")
        }
        minVisibleColumns={2}
        skeletonCount={3}
        headerKey="group_number"
        headerRender={(data) => (
          <LabelValue
            label={t("GROUP_NUMBER") + ": "}
            value={getLocalizedNumber(data.group_number)}
          />
        )}
      />

      <ImportDepartureGroupModal
        isOpen={showImportModal}
        toggle={() => setShowImportModal(false)}
      />
    </Container>
  );
};

export default DepartureGroups;
